import React, { useEffect, useRef, useState } from "react";
import {
  Sparkles,
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import ApiURl from "../controllers/Api";

gsap.registerPlugin(ScrollTrigger);

function LotusIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M32 51C22 44 18 36 20 25C27 29 31 35 32 44C33 35 37 29 44 25C46 36 42 44 32 51Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M31.8 43C24 38 20 31 22 20C29 24 32 31 31.8 43Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M32.2 43C40 38 44 31 42 20C35 24 32 31 32.2 43Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M32 37C27 30 27 22 32 13C37 22 37 30 32 37Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M31 48C21 49 13 45 8 37C19 36 27 39 31 48Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M33 48C43 49 51 45 56 37C45 36 37 39 33 48Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HeadingDivider({ small = false }) {
  return (
    <div
      className={`team-heading-divider ${
        small ? "team-heading-divider-small" : ""
      }`}
      aria-hidden="true"
    >
      <span />
      <i />
      <span />
    </div>
  );
}

function TeamImage({ member }) {
  return (
    <div className="team-image-shell">
      <div className="team-image-glow" />

      <div className="team-image-frame">
        <img src={member.image} alt={member.name} loading="lazy" />
        <div className="team-image-overlay" />
      </div>

      <div className="team-image-lotus">
        <LotusIcon />
      </div>
    </div>
  );
}

function TeamDescription({ text, desktop = false }) {
  const [expanded, setExpanded] = useState(false);
  const cleanText = String(text || "").trim();

  if (!cleanText) {
    return null;
  }

  // Desktop:
  // Do NOT trim by words/characters because that produces 3, 4, or 5 visual lines
  // depending on word length. CSS line-clamp controls the visible line count instead.
  if (desktop) {
    const shouldAllowExpand = cleanText.length > 95;

    return (
      <>
        <p
          className={`team-description desktop-team-description ${
            expanded ? "is-expanded" : ""
          }`}
        >
          {cleanText}
        </p>

        {shouldAllowExpand && (
          <button
            type="button"
            className="team-read-more"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Read Less" : "Read More"}
          </button>
        )}
      </>
    );
  }

  // Mobile behavior stays the same as before.
  const words = cleanText.split(/\s+/).filter(Boolean);
  const hasLongText = cleanText.length > 150;
  const shouldTrim = words.length > 18 || hasLongText;

  const visibleText =
    !shouldTrim || expanded
      ? cleanText
      : words.length > 18
        ? `${words.slice(0, 18).join(" ")}...`
        : `${cleanText.slice(0, 150)}...`;

  return (
    <>
      <p className="team-description">{visibleText}</p>

      {shouldTrim && (
        <button
          type="button"
          className="team-read-more"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Read Less" : "Read More"}
        </button>
      )}
    </>
  );
}

function DesktopTeamCard({ member, index, rowCompact }) {
  return (
    <article
      className={`desktop-team-card team-card-reveal ${
        rowCompact ? "desktop-team-card-compact-row" : ""
      }`}
      style={{ "--card-index": index }}
    >
      <div className="team-card-shine" />

      <TeamImage member={member} />

      <div className="desktop-team-content">
        <h2>{member.name}</h2>

        <p className="team-role">{member.role}</p>

        <HeadingDivider small />

        <TeamDescription text={member.description} desktop />
      </div>
    </article>
  );
}

function MobileTeamCard({ member, index }) {
  return (
    <article
      className="mobile-team-card mobile-team-reveal"
      style={{ "--mobile-index": index }}
    >
      <div className="mobile-card-light" />

      <div className="mobile-image-column">
        <TeamImage member={member} />
      </div>

      <div className="mobile-team-content">
        <h2>{member.name}</h2>

        <HeadingDivider small />

        <p className="team-role">{member.role}</p>

        <TeamDescription text={member.description} />
      </div>
    </article>
  );
}

const DESKTOP_COLUMNS = 4;

function getDesktopRowCompactMap(members) {
  const rowMap = new Map();

  for (let start = 0; start < members.length; start += DESKTOP_COLUMNS) {
    const rowMembers = members.slice(start, start + DESKTOP_COLUMNS);

    // A row is compact ONLY when every member in that row has no description.
    const compact = rowMembers.every(
      (member) => !String(member?.description || "").trim()
    );

    rowMembers.forEach((_, offset) => {
      rowMap.set(start + offset, compact);
    });
  }

  return rowMap;
}

export default function Teams() {
  const rootRef = useRef(null);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    let mounted = true;

    fetch(`${ApiURl}getTeamMembers.php?active_only=1`)
      .then((response) => response.json())
      .then((response) => {
        if (!mounted || !response?.success || !Array.isArray(response.data)) return;
        const members = response.data
          .filter((member) => member?.name)
          .map((member) => ({
            name: member.name || "",
            role: member.role || "",
            description: member.description || "",
            image: member.image_url || member.image_path || "",
          }))
          .filter((member) => member.image);

        setTeamMembers(members);
      })
      .catch((error) => {
        console.error("Unable to load team members:", error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useGSAP(
    () => {
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (reduceMotion) {
        gsap.set(
          [
            ".team-heading-reveal",
            ".team-subtitle-reveal",
            ".team-card-reveal",
            ".mobile-team-reveal",
            ".team-footer-reveal",
            ".team-side-flower",
          ],
          {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            rotate: 0,
          }
        );

        return;
      }

      const mm = gsap.matchMedia();

      mm.add("(min-width: 769px)", () => {
        const headingTimeline = gsap.timeline({
          defaults: {
            ease: "power3.out",
          },
        });

        headingTimeline
          .fromTo(
            ".team-top-lotus",
            {
              opacity: 0,
              y: -18,
              scale: 0.7,
            },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.8,
            }
          )
          .fromTo(
            ".team-eyebrow",
            {
              opacity: 0,
              y: 24,
              letterSpacing: "0.7em",
            },
            {
              opacity: 1,
              y: 0,
              letterSpacing: "0.35em",
              duration: 0.85,
            },
            "-=0.4"
          )
          .fromTo(
            ".team-main-heading",
            {
              opacity: 0,
              y: 45,
            },
            {
              opacity: 1,
              y: 0,
              duration: 1,
            },
            "-=0.45"
          )
          .fromTo(
            ".team-heading-reveal .team-heading-divider",
            {
              opacity: 0,
              scaleX: 0.25,
            },
            {
              opacity: 1,
              scaleX: 1,
              duration: 0.9,
            },
            "-=0.55"
          )
          .fromTo(
            ".team-subtitle-reveal",
            {
              opacity: 0,
              y: 25,
            },
            {
              opacity: 1,
              y: 0,
              duration: 0.85,
            },
            "-=0.45"
          );

        gsap.fromTo(
          ".desktop-team-card",
          {
            opacity: 0,
            y: 80,
            scale: 0.92,
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            stagger: 0.14,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".desktop-team-grid",
              start: "top 82%",
              toggleActions: "play none none reverse",
            },
          }
        );

        gsap.utils.toArray(".desktop-team-card").forEach((card) => {
          const image = card.querySelector(".team-image-frame img");
          const lotus = card.querySelector(".team-image-lotus");

          gsap.to(image, {
            yPercent: 7,
            scale: 1.08,
            ease: "none",
            scrollTrigger: {
              trigger: card,
              start: "top bottom",
              end: "bottom top",
              scrub: 1,
            },
          });

          gsap.fromTo(
            lotus,
            {
              opacity: 0,
              rotate: -30,
              scale: 0.5,
            },
            {
              opacity: 1,
              rotate: 0,
              scale: 1,
              duration: 0.75,
              ease: "back.out(1.7)",
              scrollTrigger: {
                trigger: card,
                start: "top 78%",
                toggleActions: "play none none reverse",
              },
            }
          );
        });

        gsap.fromTo(
          ".team-footer-reveal",
          {
            opacity: 0,
            y: 35,
          },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".team-footer",
              start: "top 91%",
              toggleActions: "play none none reverse",
            },
          }
        );

        gsap.to(".team-side-flower-left", {
          rotate: 14,
          y: -12,
          ease: "none",
          scrollTrigger: {
            trigger: ".team-premium-section",
            start: "top top",
            end: "bottom top",
            scrub: 1.4,
          },
        });

        gsap.to(".team-side-flower-right", {
          rotate: -14,
          y: 12,
          ease: "none",
          scrollTrigger: {
            trigger: ".team-premium-section",
            start: "top top",
            end: "bottom top",
            scrub: 1.4,
          },
        });
      });

      mm.add("(max-width: 768px)", () => {
        gsap.fromTo(
          ".team-heading-reveal",
          {
            opacity: 0,
            y: 36,
          },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out",
          }
        );

        gsap.fromTo(
          ".team-subtitle-reveal",
          {
            opacity: 0,
            y: 20,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: 0.25,
            ease: "power3.out",
          }
        );

        gsap.utils.toArray(".mobile-team-card").forEach((card, index) => {
          const direction = index % 2 === 0 ? -45 : 45;

          gsap.fromTo(
            card,
            {
              opacity: 0,
              x: direction,
              y: 24,
              scale: 0.96,
            },
            {
              opacity: 1,
              x: 0,
              y: 0,
              scale: 1,
              duration: 0.9,
              ease: "power3.out",
              scrollTrigger: {
                trigger: card,
                start: "top 89%",
                toggleActions: "play none none reverse",
              },
            }
          );

          const image = card.querySelector(".team-image-frame img");

          gsap.to(image, {
            yPercent: 6,
            scale: 1.07,
            ease: "none",
            scrollTrigger: {
              trigger: card,
              start: "top bottom",
              end: "bottom top",
              scrub: 1,
            },
          });
        });

        gsap.fromTo(
          ".team-footer-reveal",
          {
            opacity: 0,
            y: 30,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".team-footer",
              start: "top 92%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      const refreshTimer = window.setTimeout(() => {
        ScrollTrigger.refresh();
      }, 500);

      return () => {
        window.clearTimeout(refreshTimer);
        mm.revert();
      };
    },
    {
      scope: rootRef,
      dependencies: [teamMembers],
      revertOnUpdate: true,
    }
  );

  const desktopRowCompactMap = getDesktopRowCompactMap(teamMembers);

  return (
    <main
      ref={rootRef}
      className="teams-page"
      style={{ backgroundColor: "var(--user-bg, #E8D9D5)" }}
    >
      <section className="team-premium-section">
        <div className="team-background-glow team-background-glow-one" />
        <div className="team-background-glow team-background-glow-two" />
        <div className="team-noise" />

        <LotusIcon className="team-side-flower team-side-flower-left" />
        <LotusIcon className="team-side-flower team-side-flower-right" />

        <div className="team-gold-curve team-gold-curve-left" />
        <div className="team-gold-curve team-gold-curve-right" />

        <header className="team-header">
          <div className="team-heading-reveal">
            <div className="team-top-ornament">
              <span />
              <LotusIcon className="team-top-lotus" />
              <span />
            </div>

            <p className="team-eyebrow">Our Team</p>

            <h1 className="team-main-heading">
              The Minds Behind the{" "}
              <span>Elegance</span>
            </h1>
          </div>

          <p className="team-subtitle team-subtitle-reveal">
           At SL Gold, our team is our strength. Every piece of jewellery we create is a reflection of the skill, vision, and dedication of our people. From design to production, from management to client service — every member plays a vital role in upholding our legacy of quality, trust, and craftsmanship.
          </p>
        </header>

        <div className="desktop-team-grid">
          {teamMembers.map((member, index) => (
            <DesktopTeamCard
              key={member.name}
              member={member}
              index={index}
              rowCompact={desktopRowCompactMap.get(index) === true}
            />
          ))}
        </div>

        <div className="mobile-team-list">
          {teamMembers.map((member, index) => (
            <MobileTeamCard
              key={member.name}
              member={member}
              index={index}
            />
          ))}
        </div>

        <footer className="team-footer team-footer-reveal">
          <div className="team-footer-divider">
            <span />
            <Sparkles />
            <span />
          </div>

          <p className="team-footer-title">
            United by passion. Driven by values.
          </p>

          <p className="team-footer-script">
            Crafting trust. Creating memories.
          </p>

          <div className="team-footer-ornament">
            <span />
            <i />
            <i />
            <i />
            <span />
          </div>
        </footer>

        <style>{`
          .teams-page,
          .teams-page * {
            box-sizing: border-box;
          }

          .teams-page {
            width: 100%;
            min-height: 100vh;
            padding: 0;
            overflow: hidden;
            font-family: Georgia, "Times New Roman", serif;
          }

          .team-premium-section {
            --team-maroon: #240000;
            --team-maroon-deep: #110000;
            --team-maroon-soft: #3a0506;
            --team-gold: #c99832;
            --team-gold-bright: #e5bd4f;
            --team-gold-muted: #a97d22;
            --team-cream: #fff4e4;

            position: relative;
            width: 100%;
            min-height: 840px;
            margin: 0;
            padding: 32px 64px 0;
            overflow: hidden;
            border: 0;
            border-radius: 0;
            background: var(--user-bg, #E8D9D5);
            box-shadow:
              inset 0 0 70px rgba(255, 255, 255, 0.12);
          }

          .team-premium-section::before {
            display: none;
          }

          .team-noise {
            display: none;
          }

          .team-background-glow {
            display: none;
          }

          .team-background-glow-one {
            top: -120px;
            left: 12%;
            width: 420px;
            height: 270px;
            background: rgba(116, 11, 19, 0.3);
          }

          .team-background-glow-two {
            right: 6%;
            bottom: -110px;
            width: 390px;
            height: 280px;
            background: rgba(105, 8, 14, 0.24);
          }

          .team-side-flower {
            position: absolute;
            z-index: 2;
            width: 210px;
            height: 210px;
            color: rgba(201, 152, 50, 0.54);
            pointer-events: none;
            will-change: transform;
          }

          .team-side-flower-left {
            top: 175px;
            left: -89px;
            transform: rotate(-18deg);
          }

          .team-side-flower-right {
            top: 175px;
            right: -89px;
            transform: rotate(18deg);
          }

          .team-gold-curve {
            position: absolute;
            z-index: 2;
            bottom: -215px;
            width: 630px;
            height: 490px;
            border: 5px solid var(--team-gold);
            border-radius: 50%;
            background: #f8eee3;
            pointer-events: none;
          }

          .team-gold-curve::before {
            content: "";
            position: absolute;
            inset: 16px;
            border: 1px solid rgba(201, 152, 50, 0.55);
            border-radius: inherit;
          }

          .team-gold-curve-left {
            left: -390px;
          }

          .team-gold-curve-right {
            right: -390px;
          }

          .team-header {
            position: relative;
            z-index: 10;
            max-width: 1120px;
            margin: 0 auto 52px;
            text-align: center;
          }

          .team-top-ornament {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 13px;
            width: min(310px, 72%);
            margin: 0 auto 12px;
            color: var(--team-gold-bright);
          }

          .team-top-ornament span {
            height: 1px;
            flex: 1;
            background: linear-gradient(
              90deg,
              transparent,
              currentColor
            );
          }

          .team-top-ornament span:last-child {
            background: linear-gradient(
              90deg,
              currentColor,
              transparent
            );
          }

          .team-top-lotus {
            width: 48px;
            height: 48px;
            flex: none;
          }

          .team-eyebrow {
            margin: 0 0 10px;
            color: var(--team-gold-bright);
            font-family: Arial, Helvetica, sans-serif;
            font-size: clamp(13px, 1.2vw, 18px);
            font-weight: 500;
            line-height: 1;
            letter-spacing: 0.35em;
            text-transform: uppercase;
          }

          .team-main-heading {
            margin: 0;
            color: var(--user-maroon, #2B0000);
            font-size: clamp(40px, 4.1vw, 66px);
            font-weight: 400;
            line-height: 1.02;
            letter-spacing: -0.025em;
            text-shadow: 0 6px 20px rgba(255, 255, 255, 0.34);
          }

          .team-main-heading span {
            color: var(--team-gold-bright);
          }

          .team-heading-divider {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            width: min(480px, 72%);
            margin: 24px auto 0;
            color: var(--team-gold);
            transform-origin: center;
          }

          .team-heading-divider span {
            height: 1px;
            flex: 1;
            background: linear-gradient(
              90deg,
              transparent,
              currentColor 22%,
              currentColor
            );
          }

          .team-heading-divider span:last-child {
            background: linear-gradient(
              90deg,
              currentColor,
              currentColor 78%,
              transparent
            );
          }

          .team-heading-divider i {
            display: block;
            width: 14px;
            height: 14px;
            border: 2px solid currentColor;
            transform: rotate(45deg);
          }

          .team-heading-divider-small {
            width: min(155px, 70%);
            margin-top: 12px;
          }

          .team-heading-divider-small i {
            width: 8px;
            height: 8px;
            border-width: 1px;
          }

          .team-subtitle {
            max-width: 790px;
            margin: 28px auto 0;
            color: rgba(43, 0, 0, 0.78);
            font-size: clamp(14px, 1.05vw, 17px);
            line-height: 1.5;
          }

          .desktop-team-grid {
            position: relative;
            z-index: 10;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            align-items: stretch;
            gap: 25px;
            width: 100%;
            margin: 0 auto;
          }

          .desktop-team-card {
            position: relative;
            display: flex;
            min-width: 0;
            min-height: 580px;
            height: 100%;
            flex-direction: column;
            padding: 28px 25px 27px;
            overflow: hidden;
            border: 1px solid rgba(229, 189, 79, 0.92);
            border-radius: 26px;
            background:
              radial-gradient(
                circle at 50% 7%,
                rgba(108, 9, 14, 0.56),
                transparent 36%
              ),
              linear-gradient(
                155deg,
                rgba(54, 3, 5, 0.98),
                rgba(27, 0, 1, 0.99)
              );
            box-shadow:
              0 22px 42px rgba(0, 0, 0, 0.28),
              inset 0 0 40px rgba(109, 8, 13, 0.12);
            transition:
              transform 350ms ease,
              box-shadow 350ms ease,
              border-color 350ms ease;
            will-change: transform;
          }

          .desktop-team-card:hover {
            z-index: 15;
            border-color: var(--team-gold-bright);
            transform: translateY(-10px);
            box-shadow:
              0 30px 60px rgba(0, 0, 0, 0.42),
              0 0 34px rgba(201, 152, 50, 0.09);
          }

          /* Desktop only: a whole row becomes compact only when every member in that row has no description. */
          .desktop-team-card-compact-row {
            min-height: 0;
            height: 100%;
            padding-bottom: 24px;
          }

          .desktop-team-card-compact-row .desktop-team-content {
            min-height: 0;
            flex: 0 0 auto;
          }

          .team-card-shine {
            position: absolute;
            top: -50%;
            left: -100%;
            width: 60%;
            height: 210%;
            z-index: 2;
            opacity: 0;
            pointer-events: none;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(255, 233, 174, 0.09),
              transparent
            );
            transform: rotate(18deg);
            transition:
              left 650ms ease,
              opacity 250ms ease;
          }

          .desktop-team-card:hover .team-card-shine {
            left: 140%;
            opacity: 1;
          }

          .team-image-shell {
            position: relative;
            z-index: 4;
            width: min(230px, 88%);
            margin: 0 auto 30px;
            aspect-ratio: 1;
          }

          .team-image-glow {
            position: absolute;
            inset: -11px;
            border-radius: 50%;
            background: rgba(201, 152, 50, 0.07);
            filter: blur(13px);
          }

          .team-image-frame {
            position: absolute;
            inset: 0;
            overflow: hidden;
            border: 3px solid var(--team-gold-bright);
            border-radius: 50%;
            background: #260000;
            box-shadow:
              0 14px 32px rgba(0, 0, 0, 0.32),
              inset 0 0 0 5px rgba(201, 152, 50, 0.08);
          }

          .team-image-frame img {
            width: 100%;
            height: 112%;
            object-fit: cover;
            object-position: center top;
            transform-origin: center;
            transition: transform 450ms ease;
            will-change: transform;
          }

          .desktop-team-card:hover .team-image-frame img {
            transform: scale(1.07);
          }

          .team-image-overlay {
            position: absolute;
            inset: 0;
            background:
              linear-gradient(
                0deg,
                rgba(28, 0, 1, 0.44),
                transparent 48%
              ),
              radial-gradient(
                circle at 50% 30%,
                transparent 45%,
                rgba(25, 0, 1, 0.22)
              );
          }

          .team-image-lotus {
            position: absolute;
            right: 50%;
            bottom: -23px;
            display: grid;
            width: 58px;
            height: 58px;
            place-items: center;
            border: 2px solid var(--team-gold-bright);
            border-radius: 50%;
            color: var(--team-gold-bright);
            background: var(--team-maroon-deep);
            transform: translateX(50%);
            box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
          }

          .team-image-lotus svg {
            width: 37px;
            height: 37px;
          }

          .desktop-team-content {
            position: relative;
            z-index: 4;
            display: flex;
            width: 100%;
            min-width: 0;
            min-height: 275px;
            flex: 1;
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .desktop-team-content h2,
          .mobile-team-content h2 {
            margin: 0;
            color: var(--team-gold-bright);
            font-size: clamp(29px, 2.3vw, 39px);
            font-weight: 400;
            line-height: 1.08;
          }

          .team-role {
            margin: 10px 0 0;
            color: rgba(255, 255, 255, 0.95);
            font-family: Arial, Helvetica, sans-serif;
            font-size: clamp(12px, 1vw, 15px);
            font-weight: 500;
            line-height: 1.3;
            letter-spacing: 0.17em;
            text-transform: uppercase;
          }

          .team-description {
            max-width: 100%;
            margin: 17px 0 23px;
            color: rgba(255, 244, 228, 0.9);
            font-size: clamp(14px, 1.1vw, 17px);
            line-height: 1.52;
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          /*
           * Desktop description alignment:
           * Every collapsed description occupies exactly 3 visual lines.
           * This fixes the uneven 3/4/5-line ellipsis caused by JS word-count trimming.
           */
          .desktop-team-content .desktop-team-description {
            display: -webkit-box;
            min-height: calc(1.52em * 3);
            max-height: calc(1.52em * 3);
            margin-bottom: 23px;
            overflow: hidden;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            line-clamp: 3;
          }

          .desktop-team-content .desktop-team-description.is-expanded {
            display: block;
            min-height: 0;
            max-height: none;
            overflow: visible;
            -webkit-line-clamp: unset;
            line-clamp: unset;
          }

          .team-read-more {
            margin-top: auto;
            flex-shrink: 0;
            border: 0;
            border-bottom: 1px solid currentColor;
            background: transparent;
            color: var(--team-gold-bright);
            cursor: pointer;
            font-family: inherit;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            transition: color 220ms ease;
          }

          .team-read-more:hover {
            color: var(--team-cream);
          }

          .mobile-team-list {
            display: none;
          }

          .team-footer {
            position: relative;
            z-index: 10;
            max-width: 850px;
            margin: 36px auto 0;
            text-align: center;
          }

          .team-footer-divider {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            width: min(460px, 80%);
            margin: 0 auto 18px;
            color: var(--team-gold);
          }

          .team-footer-divider span {
            height: 1px;
            flex: 1;
            background: linear-gradient(
              90deg,
              transparent,
              currentColor
            );
          }

          .team-footer-divider span:last-child {
            background: linear-gradient(
              90deg,
              currentColor,
              transparent
            );
          }

          .team-footer-divider svg {
            width: 20px;
            height: 20px;
          }

          .team-footer-title {
            margin: 0;
            color: var(--team-gold-bright);
            font-family: Arial, Helvetica, sans-serif;
            font-size: clamp(15px, 1.45vw, 21px);
            letter-spacing: 0.23em;
            text-transform: uppercase;
          }

          .team-footer-script {
            margin: 7px 0 14px;
            color: var(--team-gold-bright);
            font-family: "Brush Script MT", "Segoe Script", cursive;
            font-size: clamp(27px, 2.7vw, 43px);
            line-height: 1;
          }

          .team-footer-ornament {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;
            width: min(340px, 72%);
            margin: 0 auto;
            color: var(--team-gold);
          }

          .team-footer-ornament span {
            height: 1px;
            flex: 1;
            background: linear-gradient(
              90deg,
              transparent,
              currentColor
            );
          }

          .team-footer-ornament span:last-child {
            background: linear-gradient(
              90deg,
              currentColor,
              transparent
            );
          }

          .team-footer-ornament i {
            display: block;
            width: 8px;
            height: 8px;
            border: 1px solid currentColor;
            border-radius: 50%;
          }

          .team-footer-ornament i:nth-of-type(2) {
            width: 15px;
            height: 15px;
            border-radius: 50% 0 50% 0;
            transform: rotate(45deg);
          }

          @media (max-width: 1280px) and (min-width: 769px) {
            .team-premium-section {
              padding-inline: 34px;
            }

            .desktop-team-grid {
              gap: 16px;
            }

            .desktop-team-card {
              min-height: 550px;
              padding-inline: 18px;
            }

            .desktop-team-card-compact-row {
              min-height: 0;
              height: 100%;
            }

            .team-image-shell {
              width: min(200px, 88%);
            }

            .desktop-team-content h2 {
              font-size: 29px;
            }

            .team-description {
              font-size: 14px;
            }
          }

          @media (max-width: 980px) and (min-width: 769px) {
            .desktop-team-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .desktop-team-card {
              min-height: 540px;
            }

            .desktop-team-card-compact-row {
              min-height: 0;
              height: 100%;
            }

            .team-image-shell {
              width: 215px;
            }

            .team-main-heading {
              font-size: 46px;
            }
          }

          @media (max-width: 768px) {
            .teams-page {
              padding: 0;
            }

            .team-premium-section {
              width: 100%;
              padding: 28px 15px 0;
              border-radius: 0;
            }

            .team-premium-section::before {
              display: none;
            }

            .team-side-flower {
              width: 125px;
              height: 125px;
              opacity: 0.5;
            }

            .team-side-flower-left {
              top: 75px;
              left: -63px;
            }

            .team-side-flower-right {
              top: 75px;
              right: -63px;
            }

            .team-gold-curve {
              display: none;
            }

            .team-header {
              margin-bottom: 30px;
            }

            .team-top-ornament {
              width: 190px;
              margin-bottom: 10px;
            }

            .team-top-lotus {
              width: 39px;
              height: 39px;
            }

            .team-eyebrow {
              font-size: 15px;
              letter-spacing: 0.35em;
            }

            .team-main-heading {
              max-width: 620px;
              margin-inline: auto;
              font-size: clamp(40px, 11.3vw, 65px);
              line-height: 0.98;
            }

            .team-main-heading span {
              display: inline;
            }

            .team-heading-divider {
              width: 74%;
              margin-top: 19px;
            }

            .team-subtitle {
              max-width: 560px;
              margin-top: 17px;
              padding-inline: 8px;
              font-size: 16px;
              line-height: 1.48;
            }

            .desktop-team-grid {
              display: none;
            }

            .mobile-team-list {
              position: relative;
              z-index: 10;
              display: flex;
              flex-direction: column;
              gap: 14px;
            }

            .mobile-team-card {
              position: relative;
              display: grid;
              grid-template-columns: minmax(145px, 36%) 1fr;
              gap: 20px;
              min-height: 272px;
              padding: 20px 24px 20px 20px;
              overflow: hidden;
              border: 1px solid rgba(229, 189, 79, 0.9);
              border-radius: 24px;
              background:
                radial-gradient(
                  circle at 77% 25%,
                  rgba(112, 9, 15, 0.48),
                  transparent 40%
                ),
                linear-gradient(
                  135deg,
                  rgba(53, 3, 5, 0.99),
                  rgba(24, 0, 1, 0.99)
                );
              box-shadow:
                0 18px 35px rgba(0, 0, 0, 0.3),
                inset 0 0 35px rgba(97, 7, 12, 0.1);
            }

            .mobile-card-light {
              position: absolute;
              top: -70%;
              right: -30%;
              width: 55%;
              height: 220%;
              opacity: 0.08;
              pointer-events: none;
              background: linear-gradient(
                90deg,
                transparent,
                rgba(255, 235, 184, 0.8),
                transparent
              );
              transform: rotate(20deg);
            }

            .mobile-image-column {
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .mobile-team-card .team-image-shell {
              width: 100%;
              max-width: 190px;
              margin: 0;
            }

            .mobile-team-card .team-image-lotus {
              width: 52px;
              height: 52px;
              bottom: -18px;
            }

            .mobile-team-card .team-image-lotus svg {
              width: 32px;
              height: 32px;
            }

            .mobile-team-content {
              position: relative;
              z-index: 3;
              display: flex;
              min-width: 0;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              text-align: left;
            }

            .mobile-team-content h2 {
              font-size: clamp(30px, 6.5vw, 43px);
            }

            .mobile-team-content .team-heading-divider-small {
              width: 145px;
              margin: 10px 0 12px;
            }

            .mobile-team-content .team-role {
              margin: 0;
              font-size: 13px;
              line-height: 1.4;
              letter-spacing: 0.17em;
            }

            .mobile-team-content .team-description {
              max-width: 520px;
              margin: 11px 0 15px;
              font-size: 15px;
              line-height: 1.47;
              text-align: left;
            }

            .team-footer {
              margin-top: 28px;
            }

            .team-footer-divider {
              width: 70%;
              margin-bottom: 14px;
            }

            .team-footer-title {
              font-size: 14px;
              line-height: 1.5;
              letter-spacing: 0.19em;
            }

            .team-footer-script {
              font-size: clamp(28px, 8vw, 39px);
            }
          }

          @media (max-width: 560px) {
            .team-premium-section {
              padding-inline: 12px;
            }

            .team-main-heading {
              max-width: 390px;
              font-size: clamp(38px, 11.8vw, 54px);
            }

            .team-main-heading span {
              display: inline;
            }

            .team-subtitle {
              font-size: 14px;
            }

            .mobile-team-card {
              grid-template-columns: 118px minmax(0, 1fr);
              gap: 14px;
              min-height: 245px;
              padding: 17px 16px 17px 14px;
              border-radius: 19px;
            }

            .mobile-team-card .team-image-shell {
              width: 112px;
            }

            .mobile-team-card .team-image-frame {
              border-width: 2px;
            }

            .mobile-team-card .team-image-lotus {
              width: 43px;
              height: 43px;
              bottom: -15px;
              border-width: 1px;
            }

            .mobile-team-card .team-image-lotus svg {
              width: 27px;
              height: 27px;
            }

            .mobile-team-content h2 {
              font-size: clamp(25px, 7vw, 34px);
            }

            .mobile-team-content .team-heading-divider-small {
              width: 105px;
              margin-block: 8px 9px;
            }

            .mobile-team-content .team-role {
              font-size: 10px;
              letter-spacing: 0.13em;
            }

            .mobile-team-content .team-description {
              margin-block: 9px 12px;
              font-size: 11.5px;
              line-height: 1.43;
            }

            .mobile-team-content .team-read-more {
              font-size: 10px;
            }
          }

          @media (max-width: 390px) {
            .teams-page {
              padding-inline: 0;
            }

            .team-premium-section {
              padding-inline: 9px;
            }

            .mobile-team-card {
              grid-template-columns: 102px minmax(0, 1fr);
              gap: 11px;
              min-height: 226px;
              padding: 15px 12px;
            }

            .mobile-team-card .team-image-shell {
              width: 98px;
            }

            .mobile-team-content h2 {
              font-size: 24px;
            }

            .mobile-team-content .team-description {
              font-size: 10.5px;
            }
          }
        `}</style>
      </section>
    </main>
  );
}